// src/utils/SessionManager.js

import {
  fetchSavedKanji,
  saveKanjiToSupabase,
  deleteKanjiFromSupabase,
} from "../utils/supabase/kanjiHandlers";

/* -------------------------
   🧠 In-Memory Cache (fast path)
-------------------------- */
let mem = {
  lessons: null,
  lessonsFetchedAt: 0,
};

const CACHE_TTL_MS = 60 * 1000; // 1 minute

/* -------------------------
   🗝️ Keys
-------------------------- */
const KEYS = {
  SCAN_COUNT: "scanCount",
  LAST_SCAN: "lastScan",
  JAPANGLISH_MODE: "japanglishMode",
  ANNOTATED_IMAGE: "annotatedImage",
  CLOUD_KANJI_LIST: "myCloudKanjiList",
  LANG_CODE: "aiLangCode",
  EDITED_JP: "editedJapanese",
  EDITED_EN: "editedEnglish",
  SAVED_VISION_ENTRY: "savedVisionEntry",
  SAVED_AI_CONTEXT: "savedAIContext",
  VISION_AI_CONTEXT_VISIBLE: "visionAiContextVisible",
  SHOW_JAPANESE_LABELS: "showJapaneseLabels",
  STICKY_TABS: "stickyTabs",
  GENERATE_COUNT: "generateCount",
  LAST_GENERATED_HASH: "lastGeneratedHash",
  ACTIVE_TAB: "activeTab",
  QUERY_HISTORY: "queryHistory",
  FREE_TEXT_COUNT: "freeTextCount",

  SAVED_LESSONS: "savedLessons",
  SAVED_LESSONS_TS: "savedLessons_ts",
};

/* -------------------------
   🧩 SSR-safe Storage Access
-------------------------- */
const hasWindow = () => typeof window !== "undefined";

const getSession = () => {
  if (!hasWindow()) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getLocal = () => {
  if (!hasWindow()) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const sget = (k) => {
  const s = getSession();
  if (!s) return null;
  try {
    return s.getItem(k);
  } catch {
    return null;
  }
};

const sset = (k, v) => {
  const s = getSession();
  if (!s) return;
  try {
    s.setItem(k, v);
  } catch {}
};

const srm = (k) => {
  const s = getSession();
  if (!s) return;
  try {
    s.removeItem(k);
  } catch {}
};

const lget = (k) => {
  const l = getLocal();
  if (!l) return null;
  try {
    return l.getItem(k);
  } catch {
    return null;
  }
};

const lset = (k, v) => {
  const l = getLocal();
  if (!l) return;
  try {
    l.setItem(k, v);
  } catch {}
};

const lrm = (k) => {
  const l = getLocal();
  if (!l) return;
  try {
    l.removeItem(k);
  } catch {}
};

/* -------------------------
   🧩 Helpers
-------------------------- */
const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

function stripLanguageHeaders(text) {
  if (!text || typeof text !== "string") return text;

  return text
    .replace(
      /^\s*(English|Japanese|German|Spanish|Portuguese|Italian|Indonesian)\s*:\s*/i,
      ""
    )
    .replace(/\n\s*(English|Japanese)\s*:\s*/gi, "\n")
    .trim();
}

/* -------------------------
   🧰 Session Manager
-------------------------- */
const SessionManager = {
  /* ----- Cache TTL helpers ----- */
  isLessonCacheFresh() {
    const ts = Number(sget(KEYS.SAVED_LESSONS_TS)) || Number(lget(KEYS.SAVED_LESSONS_TS)) || 0;
    const freshStorage = Date.now() - ts < CACHE_TTL_MS;
    const freshMemory = Date.now() - (mem.lessonsFetchedAt || 0) < CACHE_TTL_MS;
    return freshStorage || freshMemory;
  },

  /* ----- Lessons (dual storage + memory) ----- */
  getSavedLessons() {
    // 1) in-memory fast path
    if (Array.isArray(mem.lessons) && this.isLessonCacheFresh()) {
      return mem.lessons;
    }

    // 2) sessionStorage
    const s = safeParse(sget(KEYS.SAVED_LESSONS), null);
    if (Array.isArray(s) && s.length) {
      mem.lessons = s;
      mem.lessonsFetchedAt = Number(sget(KEYS.SAVED_LESSONS_TS)) || Date.now();
      return s;
    }

    // 3) localStorage fallback
    const l = safeParse(lget(KEYS.SAVED_LESSONS), []);
    if (Array.isArray(l) && l.length) {
      const ts = Number(lget(KEYS.SAVED_LESSONS_TS)) || Date.now();
      mem.lessons = l;
      mem.lessonsFetchedAt = ts;

      // hydrate session from local for this session
      sset(KEYS.SAVED_LESSONS, JSON.stringify(l));
      sset(KEYS.SAVED_LESSONS_TS, String(ts));
      return l;
    }

    return [];
  },

  setSavedLessons(lessons) {
    const arr = Array.isArray(lessons) ? lessons : [];
    const ts = Date.now();

    mem.lessons = arr;
    mem.lessonsFetchedAt = ts;

    const payload = JSON.stringify(arr);
    sset(KEYS.SAVED_LESSONS, payload);
    sset(KEYS.SAVED_LESSONS_TS, String(ts));
    lset(KEYS.SAVED_LESSONS, payload);
    lset(KEYS.SAVED_LESSONS_TS, String(ts));
  },

  /* ----- Local-Only Lesson Saving (Guest Mode) ----- */
  saveLocalLesson(lesson) {
    // 🚫 Local-only lessons are disabled to avoid cannibalizing paid plans.
    console.warn(
      "[SessionManager] saveLocalLesson is disabled (no guest/local lesson saves)."
    );
    // preserve old behavior: return existing list unchanged
    const existing = this.getSavedLessons();
    return existing;
  },

  clearSavedLessons() {
    mem.lessons = null;
    mem.lessonsFetchedAt = 0;

    srm(KEYS.SAVED_LESSONS);
    srm(KEYS.SAVED_LESSONS_TS);
    lrm(KEYS.SAVED_LESSONS);
    lrm(KEYS.SAVED_LESSONS_TS);
  },

  /* ----- Cache bust helpers ----- */
  clearLessonCache() {
    mem.lessons = null;
    mem.lessonsFetchedAt = 0;
    srm(KEYS.SAVED_LESSONS);
    srm(KEYS.SAVED_LESSONS_TS);
  },

  setLessonCacheTimestamp(ts = 0) {
    mem.lessonsFetchedAt = ts;
    sset(KEYS.SAVED_LESSONS_TS, String(ts));
  },

  /* ----- Legacy API (compat) ----- */
  getCachedLessons() {
    return SessionManager.getSavedLessons();
  },
  setCachedLessons(lessons) {
    SessionManager.setSavedLessons(lessons);
  },

  /* ----- Scan Tracking ----- */
  getScanCount: () => parseInt(lget(KEYS.SCAN_COUNT) || "0", 10),

  incrementScanCount() {
    const n = this.getScanCount() + 1;
    lset(KEYS.SCAN_COUNT, String(n));
    lset(KEYS.LAST_SCAN, String(Date.now()));
    return n;
  },

  resetScanCount() {
    lset(KEYS.SCAN_COUNT, "0");
    lrm(KEYS.LAST_SCAN);
  },

  /* ----- Free Text Submit Tracking (pre-signup) ----- */
  getFreeTextCount() {
    return parseInt(lget(KEYS.FREE_TEXT_COUNT) || "0", 10);
  },

  incrementFreeTextCount() {
    const n = this.getFreeTextCount() + 1;
    lset(KEYS.FREE_TEXT_COUNT, String(n));
    return n;
  },

  resetFreeTextCount() {
    lrm(KEYS.FREE_TEXT_COUNT);
  },

    /* ----- Query History ----- */
  getQueryHistory() {
    return safeParse(
      lget(KEYS.QUERY_HISTORY),
      []
    );
  },

  setQueryHistory(history) {
    const value = Array.isArray(history)
      ? history
      : [];

    lset(
      KEYS.QUERY_HISTORY,
      JSON.stringify(value)
    );
  },

  clearQueryHistory() {
    lrm(KEYS.QUERY_HISTORY);
  },

  /* ----- Generate Count ----- */
  getGenerateCount: () => parseInt(sget(KEYS.GENERATE_COUNT) || "0", 10),
  setGenerateCount: (v) => sset(KEYS.GENERATE_COUNT, String(v)),

  incrementGenerateCount() {
    const v = this.getGenerateCount() + 1;
    this.setGenerateCount(v);
    return v;
  },

  resetGenerateCount: () => srm(KEYS.GENERATE_COUNT),

  /* ----- AI Context / Vision ----- */
  getSavedAIContext() {
    const ctx = safeParse(sget(KEYS.SAVED_AI_CONTEXT), null);
    if (!ctx || !ctx.data) return ctx;

    return {
      ...ctx,
      data: {
        ...ctx.data,
        englishContent: stripLanguageHeaders(ctx.data.englishContent),
        japaneseContent: stripLanguageHeaders(ctx.data.japaneseContent),
      },
    };
  },

  saveAIContext(ctx) {
    if (!ctx || typeof ctx !== "object") {
      sset(KEYS.SAVED_AI_CONTEXT, JSON.stringify(ctx));
      return;
    }

    const cleaned = {
      ...ctx,
      data: ctx.data
        ? {
            ...ctx.data,
            englishContent: stripLanguageHeaders(ctx.data.englishContent),
            japaneseContent: stripLanguageHeaders(ctx.data.japaneseContent),
          }
        : ctx.data,
    };

    sset(KEYS.SAVED_AI_CONTEXT, JSON.stringify(cleaned));
  },

  clearSavedAIContext: () => srm(KEYS.SAVED_AI_CONTEXT),

  getSavedVisionEntry: () => safeParse(sget(KEYS.SAVED_VISION_ENTRY), null),

  saveVisionEntry(hash, data) {
    sset(KEYS.SAVED_VISION_ENTRY, JSON.stringify({ hash, data }));
  },

  clearSavedVisionEntry: () => srm(KEYS.SAVED_VISION_ENTRY),

  getLastGeneratedHash: () => sget(KEYS.LAST_GENERATED_HASH),
  setLastGeneratedHash: (h) => sset(KEYS.LAST_GENERATED_HASH, h),
  clearLastGeneratedHash: () => srm(KEYS.LAST_GENERATED_HASH),

  /* ----- UI prefs ----- */
  getActiveTab: () => sget(KEYS.ACTIVE_TAB) || "text",
  setActiveTab: (t) => sset(KEYS.ACTIVE_TAB, t),
  clearActiveTab: () => srm(KEYS.ACTIVE_TAB),

  getVisionAiContextVisible: () => sget(KEYS.VISION_AI_CONTEXT_VISIBLE) === "true",
  setVisionAiContextVisible: (v) => sset(KEYS.VISION_AI_CONTEXT_VISIBLE, String(v)),

  getShowJapaneseLabels: () => sget(KEYS.SHOW_JAPANESE_LABELS) === "true",
  setShowJapaneseLabels: (v) => sset(KEYS.SHOW_JAPANESE_LABELS, String(v)),

  getJapanglishMode: () => sget(KEYS.JAPANGLISH_MODE) === "true",
  setJapanglishMode: (v) => sset(KEYS.JAPANGLISH_MODE, String(v)),

  getLangCode: () => sget(KEYS.LANG_CODE) || "en",
  setLangCode: (c) => sset(KEYS.LANG_CODE, c || "en"),

  getSelectedLanguage() {
    return sget(KEYS.LANG_CODE) || "en";
  },

  getStickyTabs: () => (lget(KEYS.STICKY_TABS) ?? "true") === "true",
  setStickyTabs: (v) => lset(KEYS.STICKY_TABS, String(v)),

  /* ----- Edited fields ----- */
  getEditedJapanese: () => sget(KEYS.EDITED_JP) || "",
  setEditedJapanese: (t) => sset(KEYS.EDITED_JP, t),

  getEditedEnglish: () => sget(KEYS.EDITED_EN) || "",
  setEditedEnglish: (t) => sset(KEYS.EDITED_EN, t),

  clearEdited() {
    srm(KEYS.EDITED_JP);
    srm(KEYS.EDITED_EN);
  },

  /* ----- Annotation + Cloud Kanji ----- */
  getAnnotatedImage: () => sget(KEYS.ANNOTATED_IMAGE),
  setAnnotatedImage: (img) => sset(KEYS.ANNOTATED_IMAGE, img),
  clearAnnotatedImage: () => srm(KEYS.ANNOTATED_IMAGE),

  getCloudKanjiList: () => safeParse(lget(KEYS.CLOUD_KANJI_LIST), []),

  setCloudKanjiList(list) {
    lset(KEYS.CLOUD_KANJI_LIST, JSON.stringify(list || []));
  },

  toggleCloudKanji(k) {
    const list = this.getCloudKanjiList();
    const next = list.includes(k) ? list.filter((x) => x !== k) : [...list, k];
    this.setCloudKanjiList(next);
    return next;
  },

  clearCloudKanjiList: () => lrm(KEYS.CLOUD_KANJI_LIST),

  /* ----- Nuclear reset ----- */
  clearAll() {
    // session keys
    [
      KEYS.SAVED_AI_CONTEXT,
      KEYS.SAVED_VISION_ENTRY,
      KEYS.LAST_GENERATED_HASH,
      KEYS.ACTIVE_TAB,
      KEYS.VISION_AI_CONTEXT_VISIBLE,
      KEYS.SHOW_JAPANESE_LABELS,
      KEYS.JAPANGLISH_MODE,
      KEYS.ANNOTATED_IMAGE,
      KEYS.EDITED_JP,
      KEYS.EDITED_EN,
      KEYS.GENERATE_COUNT,
      KEYS.SAVED_LESSONS,
      KEYS.SAVED_LESSONS_TS,
      KEYS.LANG_CODE,
    ].forEach(srm);

    // local keys
    [
      KEYS.SCAN_COUNT,
      KEYS.LAST_SCAN,
      KEYS.CLOUD_KANJI_LIST,
      KEYS.SAVED_LESSONS,
      KEYS.SAVED_LESSONS_TS,
      KEYS.STICKY_TABS,
      KEYS.FREE_TEXT_COUNT,
      KEYS.QUERY_HISTORY,
    ].forEach(lrm);

    mem.lessons = null;
    mem.lessonsFetchedAt = 0;
  },
};

/* -------------------------
   ☁️ Kanji Sync (Supabase Only — Local Independent)
   - returns remote list (simple chars)
   - does NOT mutate local list automatically
-------------------------- */
SessionManager.syncKanjiWithSupabase = async function (userId) {
  if (!userId) return [];

  try {
    const remoteList = await fetchSavedKanji(userId);

    const remoteChars = (remoteList || [])
      .map((k) => (typeof k === "string" ? k : k.kanji || k.char || ""))
      .filter(Boolean);

    console.log(`☁️ Synced ${remoteChars.length} kanji from Supabase`);
    return remoteChars;
  } catch (err) {
    console.error("❌ syncKanjiWithSupabase failed:", err);
    return [];
  }
};

/* -------------------------
   📦 Offline Kanji Queue
-------------------------- */
SessionManager.queueKanjiSync = function (action, kanji) {
  try {
    const raw = localStorage.getItem("kanjiSyncQueue") || "[]";
    const queue = JSON.parse(raw);

    queue.push({ action, kanji, timestamp: Date.now() });

    localStorage.setItem("kanjiSyncQueue", JSON.stringify(queue));
    window.dispatchEvent(new Event("kanjiQueueUpdated")); // 🔔 notify UI

    console.log(`📥 Queued kanji sync: ${action} ${kanji}`);
  } catch (err) {
    console.error("❌ queueKanjiSync failed:", err);
  }
};

export default SessionManager;
export const { getCachedLessons, setCachedLessons, isLessonCacheFresh } = SessionManager;
const ANON_SCAN_KEY = "anon_scan_count";

export function getAnonScanCount() {
  return Number(localStorage.getItem(ANON_SCAN_KEY) || 0);
}

export function incrementAnonScan() {
  const current = getAnonScanCount();
  localStorage.setItem(ANON_SCAN_KEY, current + 1);
}

export function hasAnonScansRemaining(limit = 3) {
  return getAnonScanCount() < limit;
}